import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Alert, CircularProgress, Snackbar, Divider, Tooltip,
  LinearProgress,
} from '@mui/material';
import { Refresh, FiberManualRecord, CheckCircle, Error as ErrorIcon, Cached } from '@mui/icons-material';
import { fetchRealTimeStats, fetchAuditLog, seedConfig } from '../../api/api';
import api from '../../api/api';

const MODELS = ['users', 'products', 'orders', 'notifications', 'pos_sales', 'inventory_transactions', 'audit_logs'];

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState('');
  const [responseTime, setResponseTime] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [dbStats, setDbStats] = useState({});
  const [dbLoading, setDbLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const checkHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError('');
    const start = Date.now();
    try {
      const res = await api.get('/health');
      setResponseTime(Date.now() - start);
      setHealth(res.data);
    } catch (e) {
      setHealthError(e.response?.data?.message || 'Backend unreachable');
      setHealth(null);
      setResponseTime(Date.now() - start);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadRealtime = useCallback(async () => {
    try {
      const res = await fetchRealTimeStats();
      setRealtime(res.data);
    } catch {}
  }, []);

  const loadDbStats = useCallback(async () => {
    setDbLoading(true);
    try {
      const res = await api.get('/api/admin/stats/overview');
      setDbStats(res.data?.counts || res.data || {});
    } catch {
      // Try individual fallback
      setDbStats({});
    } finally {
      setDbLoading(false);
    }
  }, []);

  const loadRecentLogs = useCallback(async () => {
    try {
      const res = await fetchAuditLog({ limit: 10 });
      setRecentLogs(res.data?.logs || res.data || []);
    } catch {
      setRecentLogs([]);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLastRefreshed(new Date());
    await Promise.allSettled([checkHealth(), loadRealtime(), loadDbStats(), loadRecentLogs()]);
  }, [checkHealth, loadRealtime, loadDbStats, loadRecentLogs]);

  useEffect(() => {
    refreshAll();
    const interval = setInterval(refreshAll, 60000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const handleSeedConfig = async () => {
    setSeeding(true);
    try {
      await seedConfig();
      showSnack('App config defaults seeded');
    } catch {
      showSnack('Seed failed', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const handleWarmCache = async () => {
    setCacheLoading(true);
    try {
      await api.post('/api/admin/refresh-cache');
      showSnack('Cache warmed successfully');
    } catch {
      showSnack('Cache warm: endpoint not available (no-op)', 'info');
    } finally {
      setCacheLoading(false);
    }
  };

  const isHealthy = health && !healthError;

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">System Health</Typography>
          <Typography variant="body2" color="text.secondary">
            {lastRefreshed ? `Last refreshed: ${lastRefreshed.toLocaleTimeString()}` : 'Monitoring backend status'}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAll}>Refresh</Button>
      </Box>

      {/* Section 1 - API Health */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>API Health</Typography>
        {healthLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">Checking API...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={isHealthy ? <CheckCircle /> : <ErrorIcon />}
              label={isHealthy ? 'OPERATIONAL' : 'DEGRADED'}
              color={isHealthy ? 'success' : 'error'}
              sx={{ fontWeight: 900, fontSize: 16, py: 2.5, px: 1 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">Response Time</Typography>
              <Typography fontWeight={700} color={responseTime > 1000 ? 'error.main' : 'success.main'}>
                {responseTime}ms
              </Typography>
            </Box>
            {health?.env && (
              <Box>
                <Typography variant="caption" color="text.secondary">Environment</Typography>
                <Typography fontWeight={700} color="text.primary">{health.env}</Typography>
              </Box>
            )}
            {health?.timestamp && (
              <Box>
                <Typography variant="caption" color="text.secondary">Server Time</Typography>
                <Typography fontWeight={700} color="text.primary">{new Date(health.timestamp).toLocaleString()}</Typography>
              </Box>
            )}
          </Box>
        )}
        {healthError && <Alert severity="error" sx={{ mt: 2 }}>{healthError}</Alert>}
      </Paper>

      {/* Section 2 - Real-time Stats */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FiberManualRecord sx={{ color: '#10b981', fontSize: 14, animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
          <Typography variant="h6" fontWeight={800} color="text.primary">Real-time Stats</Typography>
        </Box>
        <Grid container spacing={2}>
          {[
            { label: 'Active WebSocket Connections', value: realtime?.wsConnections },
            { label: 'Online Users', value: realtime?.activeUsers },
            { label: 'Online Riders', value: realtime?.activeRiders },
            { label: 'Pending Orders', value: realtime?.pendingOrders },
          ].map((m, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={900} color="text.primary">
                  {m.value !== undefined && m.value !== null ? m.value : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">{m.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Section 3 - Database Stats */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Database Collections</Typography>
        {dbLoading ? (
          <LinearProgress />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Collection</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(dbStats).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography color="text.secondary">Stats endpoint not available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.entries(dbStats).map(([key, count]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{key}</TableCell>
                      <TableCell align="right">{typeof count === 'number' ? count.toLocaleString() : count}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Section 4 - Cache Status */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="text.primary">Cache Status</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <FiberManualRecord sx={{ color: '#10b981', fontSize: 12 }} />
              <Typography variant="body2" color="success.main" fontWeight={600}>Cache is active</Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={cacheLoading ? <CircularProgress size={16} /> : <Cached />}
            onClick={handleWarmCache}
            disabled={cacheLoading}
          >
            Warm Cache
          </Button>
        </Box>
      </Paper>

      {/* Section 5 - Recent Audit Activity */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Recent Activity</Typography>
        {recentLogs.length === 0 ? (
          <Typography color="text.secondary">No recent audit activity</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentLogs.map((log, i) => (
                  <TableRow key={log._id || i}>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {log.createdAt ? new Date(log.createdAt).toLocaleTimeString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{log.actor?.name || log.actorName || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{log.resource || '-'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Section 6 - Quick Actions */}
      <Paper sx={{ p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Quick Actions</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleSeedConfig}
            disabled={seeding}
            startIcon={seeding ? <CircularProgress size={16} /> : null}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}
          >
            Seed App Config Defaults
          </Button>
          <Button variant="outlined" onClick={() => showSnack('Session cache cleared (placeholder)', 'info')}>
            Clear Session Cache
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
